import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckOutlined,
  DeleteFilled,
  FileProtectOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Dropdown, Space, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useInterval } from 'react-use'
import $api from '../../../../API'
import { PageContainer, PageHeader } from '../../../../components/page'
import { TableDateFilter } from '../../../../components/table-filters/date'
import type { Lockbox } from '../../../../domain/lockbox'
import * as constants from './constants'
import * as store from './store'
import type { LockboxesResponse } from './types'
import * as utils from './utils'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'
import { LockboxExpandableRow } from './components/expandable-row'

export const LockboxesPage = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()

  const initialState = useMemo(() => {
    return {
      ...store.initialState,
      ...utils.buildStateFromSearchParams(searchParams)
    }
  }, [searchParams])

  const [state, dispatch] = useReducer(store.reducer, initialState)

  const lockboxesQuery = useQuery({
    queryKey: [
      'lockboxes',
      {
        currentPage: state.pagination.currentPage,
        pageSize: state.pagination.pageSize,

        // searchQuery: state.searchQuery,

        filters: {
          date: state.filters.date
        }
      }
    ],

    queryFn: async ({ queryKey }) => {
      const { currentPage, pageSize, filters /**, searchQuery **/ } =
        queryKey[1] as typeof queryKey extends Array<string | infer T> ? T : never

      const dateFrom =
        typeof filters.date.from === 'string' && filters.date.from.length > 0
          ? dayjs(filters.date.from).toDate()
          : null
      const dateTo =
        typeof filters.date.to === 'string' && filters.date.to.length > 0
          ? dayjs(filters.date.to).toDate()
          : null

      const searchParams = new URLSearchParams([
        ['page', currentPage.toString()],
        ['page-size', pageSize.toString()],

        // ...(searchQuery !== undefined && searchQuery.length > 0 ? [['search', searchQuery]] : []),

        ...(dateFrom && dayjs(dateFrom).isValid()
          ? [['date-from', dayjs(dateFrom).format('YYYY-MM-DD')]]
          : []),
        ...(dateTo && dayjs(dateTo).isValid()
          ? [['date-to', dayjs(dateTo).format('YYYY-MM-DD')]]
          : [])
      ])

      const result = await $api.get<LockboxesResponse>(
        `/pinnacle/lockboxes?${searchParams.toString()}`
      )

      return result.data
    },

    keepPreviousData: true
  })

  useEffect(() => {
    const params = utils.buildSearchParams([
      {
        name: 'page',
        value: state.pagination.currentPage
      },
      {
        name: 'page-size',
        value: state.pagination.pageSize
      },
      // {
      //   name: 'search',
      //   value: state.searchQuery
      // },
      {
        name: 'date-from',
        value: state.filters.date.from
      },
      {
        name: 'date-to',
        value: state.filters.date.to
      }
    ])

    setSearchParams(params)
  }, [
    state.filters,
    state.pagination,
    // state.searchQuery,
    setSearchParams
  ])

  const handleRefresh = useCallback(() => {
    void lockboxesQuery.refetch()
  }, [lockboxesQuery])

  useInterval(() => {
    handleRefresh()
  }, state.refreshInterval ?? null)

  const tableColumns = useMemo<ColumnsType<Lockbox>>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 300,

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Extract Date',
        dataIndex: 'date',
        width: 150,

        filterDropdown: TableDateFilter,

        filteredValue:
          (typeof state.filters.date.from === 'string' && state.filters.date.from.length > 0) ||
          (typeof state.filters.date.to === 'string' && state.filters.date.to.length > 0)
            ? [state.filters.date.from ?? '', state.filters.date.to ?? '']
            : [],

        render(value: string) {
          return dayjs(value).tz('America/Chicago').format('MM/DD/YYYY HH:mm:ss')
        },

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Number',
        dataIndex: 'number',
        key: 'number',

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Name',
        dataIndex: 'shortName',
        key: 'shortName',

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Total Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        align: 'right',

        render(value: number) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(value)
        },

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      }
    ],
    [state.filters]
  )

  const isDefaultFilters = useMemo(() => {
    return !state.filters.date.from && !state.filters.date.to
  }, [state.filters])

  return (
    <PageContainer>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <PageHeader
          title='Lockboxes'
          description='Deposits and reconciliation status'
          icon={<FileProtectOutlined />}
        />

        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-row gap-2 items-center p-4'>
            <div className='flex-grow'>
              {/* <OuterTableTextFilter
                placeholder='Search by actor, action, type'
                defaultValue={searchParams.get('search') ?? undefined}
                onSearch={(value: string | undefined) => {
                  dispatch({
                    type: 'searchQuery/set',
                    payload: value
                  })
                }}
                onClear={() => {
                  dispatch({
                    type: 'searchQuery/set',
                    payload: undefined
                  })
                }}
              /> */}
            </div>

            <Space>
              {!isDefaultFilters && (
                <Button
                  type='default'
                  onClick={() => {
                    dispatch({ type: 'filters/clear' })
                  }}
                  className='flex items-center py-1'
                >
                  <DeleteFilled className='text-lucent-primary' />

                  <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase'>
                    Clear Filters
                  </span>
                </Button>
              )}

              <Dropdown.Button
                type='default'
                disabled={lockboxesQuery.isFetching}
                onClick={handleRefresh}
                icon={
                  <CaretDownOutlined
                    className={cx(
                      'w-[12px] h-[12px] !translate-y-[2px]',
                      typeof state.refreshInterval === 'number' && 'text-lucent-primary',
                      typeof state.refreshInterval !== 'number' && 'text-lucent-gray'
                    )}
                  />
                }
                menu={{
                  items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                    key: `refresh-${seconds}`,
                    label: utils.formatDuration(seconds * 1000),

                    itemIcon() {
                      if (state.refreshInterval === seconds * 1000) {
                        return <CheckOutlined className='ml-2 text-lucent-primary' />
                      }

                      return null
                    },

                    onClick() {
                      if (state.refreshInterval === seconds * 1000) {
                        dispatch({
                          type: 'refreshInterval/clear'
                        })

                        return
                      }

                      dispatch({
                        type: 'refreshInterval/set',
                        payload: seconds * 1000
                      })
                    }
                  }))
                }}
              >
                <div className='flex items-center space-x-1.5'>
                  <SyncOutlined
                    className={cx(
                      'text-base',
                      lockboxesQuery.isFetching && 'animate-custom-spin text-lucent-gray',
                      !lockboxesQuery.isFetching && 'text-lucent-primary'
                    )}
                  />

                  <span
                    className={cx(
                      'font-lato font-bold text-[10px] uppercase',
                      lockboxesQuery.isFetching && 'text-lucent-gray',
                      !lockboxesQuery.isFetching && 'text-lucent-primary'
                    )}
                  >
                    <span>Refresh</span>

                    {typeof state.refreshInterval === 'number' && (
                      <span>
                        <span className='mx-1.5'>|</span>
                        Auto {utils.formatDuration(state.refreshInterval, { short: true })}
                      </span>
                    )}
                  </span>
                </div>
              </Dropdown.Button>
            </Space>
          </div>

          <Table
            size='small'
            columns={tableColumns}
            rowKey='id'
            dataSource={lockboxesQuery.data?.data ?? []}
            loading={lockboxesQuery.isFetching}
            className='rounded-none border-t payment-files-table'
            pagination={{
              current: state.pagination.currentPage + 1,
              pageSize: state.pagination.pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              total: lockboxesQuery.data?.total ?? 0,
              showTotal(total, [from, to]) {
                const parts = [
                  `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                ]

                if (
                  typeof lockboxesQuery.data?.total === 'number' &&
                  lockboxesQuery.data.total !== total
                ) {
                  parts.push(
                    `Total ${lockboxesQuery.data.total} ${lockboxesQuery.data.total === 1 ? 'item' : 'items'}`
                  )
                }

                return <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
              },
              hideOnSinglePage: false
            }}
            onChange={(pagination, filters, _sorters, extra) => {
              if (extra.action === 'filter') {
                dispatch({
                  type: 'filters/set',
                  payload: {
                    date: {
                      from: filters.date?.[0] as string | undefined,
                      to: filters.date?.[1] as string | undefined
                    }
                  }
                })
              }

              if (extra.action === 'paginate') {
                dispatch({
                  type: 'pagination/currentPage/set',
                  payload: (pagination.current ?? 1) - 1
                })

                if ((pagination.pageSize ?? 10) !== state.pagination.pageSize) {
                  dispatch({
                    type: 'pagination/pageSize/set',
                    payload: pagination.pageSize ?? 10
                  })
                }
              }
            }}
            expandable={{
              expandedRowRender: lockbox => <LockboxExpandableRow lockboxId={lockbox.id} />,
              expandRowByClick: true,
              expandIcon: ExpandIcon
            }}
          />
        </div>
      </div>
    </PageContainer>
  )
}

const ExpandIcon = (props: RenderExpandIconProps<Lockbox>): JSX.Element | null => {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-50 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}
